import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('DatabaseConnectivity', 'system-status');

export interface DatabaseConnectivityStatus {
  connected: boolean;
  error: unknown | null;
}

/**
 * Verify that the browser can reach the Data API through the deliberately
 * public, safe profile projection. This is a connectivity check only: client
 * code cannot infer table existence, migrations, or RLS configuration by
 * probing protected base tables.
 */
export async function checkDatabaseConnectivity(
  signal?: AbortSignal
): Promise<DatabaseConnectivityStatus> {
  try {
    let query = supabase
      .from('public_profiles')
      .select('id')
      .limit(0);

    if (signal) {
      query = query.abortSignal(signal);
    }

    const { error } = await query;
    if (signal?.aborted) {
      return { connected: false, error: null };
    }

    if (error) {
      log.error(
        'Database connectivity check failed',
        undefined,
        new Error(error.message)
      );
      return { connected: false, error };
    }

    return { connected: true, error: null };
  } catch (error) {
    if (signal?.aborted) {
      return { connected: false, error: null };
    }

    log.error('Database connectivity check threw an exception', undefined, error as Error);
    return { connected: false, error };
  }
}
